import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
  Alert,
  AlertTitle,
  Divider,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as UserIcon,
  AutoAwesome as SuggestIcon,
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { sendAIMessage, ChatMessage, getAIEntitlements, getAIUpgradeInfo, AIEntitlements, UpgradeInfo } from '../services/api';

interface AIAssistantProps {
  tenantId?: string;
}

export default function AIAssistant({ tenantId }: AIAssistantProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entitlements, setEntitlements] = useState<AIEntitlements | null>(null);
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeInfo | null>(null);
  const [loadingEntitlements, setLoadingEntitlements] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const suggestedPrompts = [
    'What security policies are included in M365 E5?',
    'How do I configure BitLocker settings?',
    'Explain the difference between compliance and configuration policies',
    'What are the recommended Windows security settings?',
    'How can I protect against ransomware?',
  ];

  useEffect(() => {
    // Load entitlements
    const loadEntitlements = async () => {
      if (!tenantId) return;
      
      try {
        setLoadingEntitlements(true);
        const [entitlementsData, upgradeData] = await Promise.all([
          getAIEntitlements(tenantId),
          getAIUpgradeInfo(tenantId)
        ]);
        setEntitlements(entitlementsData);
        setUpgradeInfo(upgradeData);
      } catch (err) {
        console.error('Failed to load AI entitlements:', err);
      } finally {
        setLoadingEntitlements(false);
      }
    };

    loadEntitlements();

    // Add welcome message
    setMessages([
      {
        role: 'assistant',
        content:
          "Hello! I'm your AI assistant for Microsoft 365 security policies. I can help you understand policies, answer questions about configurations, and provide recommendations. What would you like to know?",
        timestamp: new Date().toISOString(),
      },
    ]);
  }, [tenantId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setError(null);

    // Add user message to chat
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newUserMessage]);

    if (!tenantId) {
      throw new Error('Customer ID is required');
    }

    try {
      setLoading(true);

      // Send to AI API
      const response = await sendAIMessage(tenantId, userMessage);

      // Add AI response to chat
      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to get AI response');
      
      // Add error message to chat
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setInputMessage(prompt);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        AI Assistant
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Ask questions about security policies and get expert recommendations
      </Typography>

      {/* Entitlement Status Banner */}
      {!loadingEntitlements && entitlements && !entitlements.canGeneratePolicies && upgradeInfo && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          icon={<LockIcon />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<UpgradeIcon />}
              onClick={() => window.location.href = '/deploy'}
            >
              View Configurations
            </Button>
          }
        >
          <AlertTitle>Standard AI - {entitlements.deployedConfiguration || 'No Configuration Deployed'}</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {upgradeInfo.message}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {upgradeInfo.qualifyingConfigurations?.slice(0, 4).map((config) => (
              <Chip 
                key={config} 
                label={config} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            ))}
            {upgradeInfo.qualifyingConfigurations && upgradeInfo.qualifyingConfigurations.length > 4 && (
              <Chip 
                label={`+${upgradeInfo.qualifyingConfigurations.length - 4} more`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Box>
        </Alert>
      )}

      {!loadingEntitlements && entitlements && entitlements.canGeneratePolicies && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
          <Typography variant="body2">
            <strong>Premium AI Enabled</strong> - {entitlements.deployedConfiguration} configuration includes full AI features: policy generation, modification, and deployment.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Chat Container */}
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: 2,
          overflow: 'hidden',
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              {message.role === 'assistant' && (
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AIIcon />
                </Avatar>
              )}
              
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  maxWidth: '70%',
                  bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                  color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {message.content}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: 'block',
                    opacity: 0.7,
                  }}
                >
                  {new Date(message.timestamp).toLocaleTimeString()}
                </Typography>
              </Paper>

              {message.role === 'user' && (
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <UserIcon />
                </Avatar>
              )}
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <AIIcon />
              </Avatar>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.100' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>
                  Thinking...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <Divider />

        {/* Suggested Prompts (show only when no messages or just welcome) */}
        {messages.length <= 1 && (
          <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SuggestIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Suggested questions:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {suggestedPrompts.map((prompt, index) => (
                <Chip
                  key={index}
                  label={prompt}
                  size="small"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Input Area */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask me anything about security policies..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              variant="outlined"
              size="small"
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || loading}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
            </IconButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Press Enter to send, Shift+Enter for new line
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}
